import * as React from 'react';
import { cn } from '@/lib/utils';

export type TriangleCornerPosition =
  | 'top-left'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-right';

export interface TriangleCornerProps
  extends React.HTMLAttributes<HTMLDivElement> {
  /** 三角形のサイズ（px） */
  size?: number;
  /** 三角形の色 */
  color?: string;
  /** 配置位置 */
  position?: TriangleCornerPosition;
}

/**
 * TriangleCorner - 三角形のコーナーデコレーション
 *
 * 直角三角形のコーナーデコレーションです。
 * 主にコンテナの角に装飾として使用します。
 *
 * @example
 * ```tsx
 * // デフォルト（左上）
 * <TriangleCorner />
 *
 * // サイズ指定
 * <TriangleCorner size={20} />
 *
 * // 色指定
 * <TriangleCorner color="#FF497A" />
 *
 * // 位置指定
 * <TriangleCorner position="top-right" />
 *
 * // カスタムクラス
 * <TriangleCorner className="absolute top-0 left-0" />
 * ```
 */
export const TriangleCorner = React.forwardRef<
  HTMLDivElement,
  TriangleCornerProps
>(
  (
    {
      className,
      size = 20,
      color = '#FF497A',
      position = 'top-left',
      style,
      ...props
    },
    ref,
  ) => {
    const positionStyles: Record<TriangleCornerPosition, React.CSSProperties> =
      {
        'top-left': {},
        'top-right': { transform: 'scaleX(-1)' },
        'bottom-left': { transform: 'scaleY(-1)' },
        'bottom-right': { transform: 'scale(-1, -1)' },
      };

    return (
      <div
        ref={ref}
        className={cn('inline-block', className)}
        style={{
          width: `${size}px`,
          height: `${size}px`,
          ...positionStyles[position],
          ...style,
        }}
        {...props}
      >
        <svg
          width="100%"
          height="100%"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          style={{ display: 'block' }}
        >
          <path d="M0 0H20L0 20V0Z" fill={color} />
        </svg>
      </div>
    );
  },
);

TriangleCorner.displayName = 'TriangleCorner';
